// writeQueue.js — a retry queue for sync writes that died on the network
// (2026-10-23, SCORIFY-APP-1M: daily_activity upsert "Load failed"). Pure
// queue math; src/services/writeRetry.js persists it and flushes on reconnect
// or the next app resume. Last write wins per (table, key).
export const WRITE_QUEUE_MAX_ATTEMPTS = 5;
const TRANSPORT_RE = /load failed|failed to fetch|networkerror|network request failed|the internet connection appears to be offline|timed? ?out|fetch failed|socket|ECONNRESET|ENOTFOUND/i;

/** A network/transport failure (retry later), as opposed to a server rejection (report now). */
export const isTransportFailure = (err) => TRANSPORT_RE.test(String(err?.message || err || ''));

/**
 * Add rows to the queue, replacing any queued row with the same table + key
 * (last write wins). `keyOf(row)` identifies a row (e.g. its date).
 */
export function enqueueRows(queue, { table, conflict, keyOf, rows, now = Date.now() }) {
  const out = (queue || []).filter(e => !(e.table === table && rows.some(r => keyOf(r) === e.key)));
  for (const row of rows) out.push({ table, conflict, key: keyOf(row), row, attempts: 0, queuedAt: now });
  return out;
}

/** The entries to flush now, grouped by table + conflict (one upsert each). */
export function groupForFlush(queue) {
  const groups = new Map();
  for (const e of queue || []) {
    const k = `${e.table}|${e.conflict}`;
    if (!groups.has(k)) groups.set(k, { table: e.table, conflict: e.conflict, entries: [] });
    groups.get(k).entries.push(e);
  }
  return [...groups.values()];
}

/**
 * The queue after one flush attempt of a group: success removes its entries;
 * a transport failure bumps attempts and keeps them below the cap (dropped
 * at the cap, and `exhausted` lists them so the caller can report once); a
 * server rejection drops them (nothing to retry) and reports.
 */
export function afterFlush(queue, group, outcome) {
  const ids = new Set(group.entries.map(e => `${e.table}|${e.key}`));
  const mine = (e) => ids.has(`${e.table}|${e.key}`);
  if (outcome === 'ok' || outcome === 'rejected') return { queue: (queue || []).filter(e => !mine(e)), exhausted: [] };
  const exhausted = [];
  const next = [];
  for (const e of queue || []) {
    if (!mine(e)) { next.push(e); continue; }
    const attempts = (e.attempts || 0) + 1;
    if (attempts >= WRITE_QUEUE_MAX_ATTEMPTS) exhausted.push({ ...e, attempts }); else next.push({ ...e, attempts });
  }
  return { queue: next, exhausted };
}
